/**
 * Voice AI — IVR Routes Tab
 * Toggle list of phone numbers. Assign/unassign to this IVR menu.
 * Self-loading: fetches phone numbers on mount + when menu changes.
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { authService } from '../../services/auth-service.js';

const API_BASE = '/voice/php/api';

export class IvrRoutesTab extends LitElement {
  static properties = {
    menu: { type: Object },
    _numbers: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
  };

  static styles = [designSystem, componentStyles, ivrStyles];

  constructor() {
    super();
    this.menu = null;
    this._numbers = [];
    this._loading = true;
    this._lastMenuId = null;
  }

  updated(changed) {
    if (changed.has('menu') && this.menu?.id !== this._lastMenuId) {
      this._lastMenuId = this.menu?.id;
      this._loadNumbers();
    }
  }

  async _loadNumbers() {
    this._loading = true;
    try {
      const res = await authService.apiFetch(`${API_BASE}/phone-numbers.php`);
      const data = await res.json();
      if (data.success) {
        this._numbers = data.numbers || [];
      }
    } catch (err) {
      console.error('[IvrRoutesTab] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message } }));
  }

  async _toggleAssign(number) {
    const isAssigned = number.ivr_menu_id === this.menu.id;
    const newIvrMenuId = isAssigned ? null : this.menu.id;

    try {
      const res = await authService.apiFetch(`${API_BASE}/phone-numbers.php?id=${number.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ivr_menu_id: newIvrMenuId }),
      });
      const data = await res.json();
      if (data.success) {
        this._loadNumbers();
        this._toast(isAssigned
          ? `${number.phone_number} unassigned from IVR`
          : `${number.phone_number} assigned to "${this.menu.name}"`
        );
      } else {
        this._toast('Update failed: ' + (data.error || 'Unknown'));
      }
    } catch (err) {
      this._toast('Update failed: ' + err.message);
    }
  }

  _getAssignment(number) {
    if (number.ivr_menu_id === this.menu?.id) return 'This menu';
    if (number.ivr_menu_id) return `IVR #${number.ivr_menu_id}`;
    if (number.agent_name) return `Agent: ${number.agent_name}`;
    return 'Unassigned';
  }

  render() {
    return html`
      ${iconStyles}

      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-route"></i> Phone Routes</div>
            <div class="section-description">Assign phone numbers to this IVR menu. When a call comes in on an assigned number, the IVR menu will play instead of routing directly to an agent.</div>
          </div>
        </div>

        ${this._loading ? html`
          ${[1, 2, 3].map(() => html`
            <div class="route-item">
              <div class="skeleton-line" style="width:40px;height:22px;border-radius:11px;"></div>
              <div class="route-info">
                <div class="skeleton-line" style="width:120px;height:14px;margin-bottom:6px;"></div>
                <div class="skeleton-line xs" style="width:80px;"></div>
              </div>
            </div>
          `)}
        ` : this._numbers.length === 0 ? html`
          <div class="empty-state" style="padding:var(--space-8);">
            <div class="empty-state-icon" style="font-size:var(--text-3xl);"><i class="fa-sharp fa-light fa-hashtag"></i></div>
            <div class="empty-state-title" style="font-size:var(--text-base);">No phone numbers</div>
            <div class="empty-state-description">Add phone numbers in the Phone Numbers page first.</div>
          </div>
        ` : html`
          ${this._numbers.map(num => {
            const isAssigned = num.ivr_menu_id === this.menu?.id;
            return html`
              <div class="route-item">
                <div class="toggle ${isAssigned ? 'active' : ''}"
                  @click=${() => this._toggleAssign(num)}></div>
                <div class="route-info">
                  <div class="route-number">${num.phone_number}</div>
                  <div class="route-current">
                    ${this._getAssignment(num)}
                    ${num.park_name ? html` &middot; ${num.park_name}` : ''}
                  </div>
                </div>
                ${isAssigned ? html`
                  <span class="badge badge-info" style="font-size:9px;">Assigned</span>
                ` : ''}
              </div>
            `;
          })}
        `}
      </div>
    `;
  }
}

customElements.define('ivr-routes-tab', IvrRoutesTab);
